import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { formatEuro } from "../lib/format";
import { displayName } from "../lib/anagConfig";
import { PROFORMA_REGIMI, REGIME_OPTIONS, calcProforma } from "../lib/proforma";
import { attachProformaToPratica, replaceProformaAttachment } from "../lib/proformaPdf";
import { ValidationHint } from "./ValidationHint";
import { validateCodiceFiscale } from "../lib/validators";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { FileText, Plus, Trash2, Loader2, Save, Search, User, Building2, Star, ListChecks, UserPlus, Pencil, Check, X } from "lucide-react";
import { CopiaDatiClienteButton } from "./CopiaDatiClienteButton";
import { EuroDotInput } from "./EuroDotInput";
import { toast } from "sonner";

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));
const EMPTY_DEST = { nome: "", cognome: "", ragione_sociale: "", codice_fiscale: "", partita_iva: "", codice_sdi: "", indirizzo: "", numero_civico: "", cap: "", citta: "", provincia: "", email: "", pec: "" };

function destFromRecord(rec) {
  const azienda = !!rec.denominazione;
  return {
    tipo: azienda ? "azienda" : "persona",
    dest: {
      nome: rec.nome || "", cognome: rec.cognome || "",
      ragione_sociale: rec.denominazione || rec.ragione_sociale || "",
      codice_fiscale: rec.codice_fiscale || "", partita_iva: rec.partita_iva || "",
      codice_sdi: rec.codice_destinatario || rec.codice_sdi || "",
      indirizzo: rec.indirizzo || "", numero_civico: rec.numero_civico || "",
      cap: rec.cap || "", citta: rec.comune || rec.citta || "", provincia: rec.provincia || "",
      email: rec.email || "", pec: rec.pec || "",
    },
  };
}

export default function ProformaDialog({ open, onOpenChange, pratica, client, editing, onSaved, clienteKey }) {
  const [tipo, setTipo] = useState("persona");
  const [blurred, setBlurred] = useState({});
  const [dest, setDest] = useState({ ...EMPTY_DEST });
  const [salvaAnag, setSalvaAnag] = useState(false);
  const [voci, setVoci] = useState([{ descrizione: "", quantita: "1", prezzo_unitario: "" }]);
  const [regime, setRegime] = useState("forfettario");
  const [regimeLabel, setRegimeLabel] = useState("");
  const [ivaPct, setIvaPct] = useState(0);
  const [ritPct, setRitPct] = useState(0);
  const [cassaPct, setCassaPct] = useState(0);
  const [dicitura, setDicitura] = useState("");
  const [dicModelId, setDicModelId] = useState("");
  const [note, setNote] = useState("");
  const [contoIdx, setContoIdx] = useState(0);
  const [studio, setStudio] = useState(null);
  const [saving, setSaving] = useState(false);
  const [predef, setPredef] = useState([]);
  const [predefOpen, setPredefOpen] = useState(false);
  const [editPredef, setEditPredef] = useState(null);
  // client search
  const [pickOpen, setPickOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  // autocomplete indirizzo (Nominatim / OpenStreetMap)
  const [addrSug, setAddrSug] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [addrLoading, setAddrLoading] = useState(false);
  const addrTimer = useRef();
  const [diciture, setDiciture] = useState([]);
  const dicitureRef = useRef([]);

  const setD = (k, v) => setDest((d) => ({ ...d, [k]: v }));

  const principalDicituraObj = (key) => {
    const list = dicitureRef.current || [];
    return list.find((d) => d.regime === key && d.principale) || list.find((d) => d.regime === key) || null;
  };

  const applyRegime = (key, keepCustom) => {
    const preset = PROFORMA_REGIMI[key] || PROFORMA_REGIMI.altro;
    setRegime(key);
    setRegimeLabel(preset.label || "");
    if (!keepCustom) {
      setIvaPct(preset.iva); setRitPct(preset.ritenuta); setCassaPct(preset.cassa);
      // Precompila la dicitura legale predefinita del regime (modello "principale" dello studio,
      // altrimenti il testo preset del regime). Seleziona anche il modello nel dropdown.
      const tpl = principalDicituraObj(key);
      if (tpl) { setDicitura(tpl.testo || ""); setDicModelId(tpl.id || ""); }
      else { setDicitura(preset.dicitura || ""); setDicModelId(""); }
    }
  };

  useEffect(() => {
    if (!open) return;
    api.get("/studio/me").then(({ data }) => {
      setStudio(data);
      setPredef(Array.isArray(data.settings?.proforma_voci) ? data.settings.proforma_voci : []);
      const dl = Array.isArray(data.settings?.proforma_diciture) ? data.settings.proforma_diciture : [];
      setDiciture(dl); dicitureRef.current = dl;
      if (!editing) {
        const fd = data.settings?.fatturazione_defaults || {};
        const defRegime = fd.regime_default || "forfettario";
        applyRegime(defRegime);
        if ((PROFORMA_REGIMI[defRegime] || {}).editable) {
          if (fd.iva_pct != null && fd.iva_pct !== "") setIvaPct(fd.iva_pct);
          if (fd.ritenuta_pct != null && fd.ritenuta_pct !== "") setRitPct(fd.ritenuta_pct);
          if (fd.cassa_pct != null && fd.cassa_pct !== "") setCassaPct(fd.cassa_pct);
        }
      }
    }).catch(() => {});
    setPickOpen(false); setQ(""); setResults([]); setSearched(false); setSalvaAnag(false);
    if (editing) {
      const cd = editing.cliente_dati || {};
      setTipo(cd.tipo || (cd.ragione_sociale ? "azienda" : "persona"));
      setDest({ ...EMPTY_DEST, ...cd });
      setVoci((editing.voci || [{ descrizione: "", quantita: "1", prezzo_unitario: "" }]).map((v) => ({ descrizione: v.descrizione || "", quantita: String(v.quantita ?? "1"), prezzo_unitario: String(v.prezzo_unitario ?? v.importo ?? "") })));
      setRegime(editing.regime || "forfettario"); setRegimeLabel(editing.regime_label || "");
      setIvaPct(editing.iva_pct || 0); setRitPct(editing.ritenuta_pct || 0); setCassaPct(editing.cassa_pct || 0);
      setDicitura(editing.dicitura || ""); setNote(editing.note || "");
      const _em = (dicitureRef.current || []).find((d) => d.regime === (editing.regime || "forfettario") && d.testo === editing.dicitura);
      setDicModelId(_em ? _em.id : "");
    } else {
      setVoci([{ descrizione: "", quantita: "1", prezzo_unitario: "" }]); setNote(""); applyRegime("forfettario");
      const cd = client?.cliente_dati || {};
      setTipo(cd.tipo || (cd.ragione_sociale ? "azienda" : "persona"));
      setDest({ ...EMPTY_DEST, ...cd });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const vociNorm = useMemo(() => voci.map((v) => ({ descrizione: v.descrizione, quantita: num(v.quantita) || 0, prezzo_unitario: num(v.prezzo_unitario), importo: Math.round((num(v.quantita) || 0) * num(v.prezzo_unitario) * 100) / 100 })), [voci]);
  const calc = useMemo(() => calcProforma(vociNorm, { iva_pct: num(ivaPct), ritenuta_pct: num(ritPct), cassa_pct: num(cassaPct) }), [vociNorm, ivaPct, ritPct, cassaPct]);

  const conti = ((studio?.settings || {}).conti_correnti || []).filter((c) => c && (c.iban || c.banca || c.nome));
  useEffect(() => { const di = conti.findIndex((c) => c.predefinito); setContoIdx(di >= 0 ? di : 0); /* eslint-disable-next-line */ }, [studio]);

  const setVoce = (i, k, val) => setVoci((arr) => arr.map((v, idx) => (idx === i ? { ...v, [k]: val } : v)));
  const addVoce = (v) => setVoci((arr) => [...arr, v || { descrizione: "", quantita: "1", prezzo_unitario: "" }]);
  const delVoce = (i) => setVoci((arr) => arr.filter((_, idx) => idx !== i));

  const doSearch = async (query) => {
    setSearching(true);
    try { const { data } = await api.get(`/anagrafiche/clienti?q=${encodeURIComponent(query || "")}`); setResults(data || []); }
    catch { setResults([]); }
    finally { setSearching(false); setSearched(true); }
  };
  useEffect(() => { if (!pickOpen) return; const t = setTimeout(() => doSearch(q), 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, pickOpen]);

  const chooseCliente = (rec) => { const { tipo: t, dest: d } = destFromRecord(rec); setTipo(t); setDest({ ...EMPTY_DEST, ...d }); setPickOpen(false); setQ(""); };

  const searchAddr = async (query) => {
    setAddrLoading(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&countrycodes=it&q=${encodeURIComponent(query)}`;
      const r = await fetch(url, { headers: { "Accept-Language": "it" } });
      const data = await r.json();
      setAddrSug(Array.isArray(data) ? data : []);
    } catch { setAddrSug([]); }
    finally { setAddrLoading(false); }
  };
  const onAddrChange = (val) => {
    setD("indirizzo", val);
    setAddrOpen(true);
    clearTimeout(addrTimer.current);
    if ((val || "").trim().length < 3) { setAddrSug([]); return; }
    addrTimer.current = setTimeout(() => searchAddr(val), 450);
  };
  const pickAddr = (s) => {
    const a = s.address || {};
    const road = a.road || a.pedestrian || a.footway || a.residential || a.neighbourhood || "";
    const via = [road, a.house_number].filter(Boolean).join(", ") || (s.display_name || "").split(",")[0];
    const citta = a.city || a.town || a.village || a.municipality || a.county || "";
    let prov = "";
    if (a["ISO3166-2-lvl6"]) prov = String(a["ISO3166-2-lvl6"]).replace("IT-", "");
    else if (a.county) prov = a.county;
    setDest((d) => ({ ...d, indirizzo: via, cap: a.postcode || d.cap, citta: citta || d.citta, provincia: prov || d.provincia }));
    setAddrOpen(false); setAddrSug([]);
  };

  const regimeEditable = (PROFORMA_REGIMI[regime] || {}).editable;
  const intestatario = (tipo === "azienda" ? dest.ragione_sociale : [dest.nome, dest.cognome].filter(Boolean).join(" ")).trim();

  // Salva voce come predefinita
  const saveAsPredef = async (v) => {
    const entry = { descrizione: (v.descrizione || "").trim(), quantita: num(v.quantita) || 1, prezzo_unitario: num(v.prezzo_unitario) };
    if (!entry.descrizione) { toast.error("Inserisci la descrizione prima di salvarla"); return; }
    const next = [...predef, entry];
    try { await api.patch("/studio/settings", { proforma_voci: next }); setPredef(next); toast.success("Voce salvata tra le predefinite"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const choosePredef = (p) => { addVoce({ descrizione: p.descrizione || "", quantita: String(p.quantita ?? "1"), prezzo_unitario: String(p.prezzo_unitario ?? "") }); setPredefOpen(false); };
  const persistPredef = async (next) => { try { await api.patch("/studio/settings", { proforma_voci: next }); setPredef(next); return true; } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; } };
  const delPredef = async (i) => { const next = predef.filter((_, idx) => idx !== i); if (await persistPredef(next)) { toast.success("Voce predefinita eliminata"); if (editPredef?.i === i) setEditPredef(null); } };
  const startEditPredef = (i) => setEditPredef({ i, descrizione: predef[i].descrizione || "", quantita: String(predef[i].quantita ?? 1), prezzo_unitario: String(predef[i].prezzo_unitario ?? 0) });
  const saveEditPredef = async () => { const i = editPredef.i; if (!(editPredef.descrizione || "").trim()) { toast.error("Descrizione obbligatoria"); return; } const next = predef.map((p, idx) => (idx === i ? { descrizione: editPredef.descrizione.trim(), quantita: num(editPredef.quantita) || 1, prezzo_unitario: num(editPredef.prezzo_unitario) || 0 } : p)); if (await persistPredef(next)) { toast.success("Voce predefinita aggiornata"); setEditPredef(null); } };

  const validateDest = () => {
    const miss = [];
    if (!dest.indirizzo?.trim()) miss.push("Indirizzo");
    if (!dest.cap?.trim()) miss.push("CAP");
    if (!dest.citta?.trim()) miss.push("Città");
    if (!dest.provincia?.trim()) miss.push("Provincia");
    if (tipo === "persona") {
      if (!dest.nome?.trim()) miss.push("Nome");
      if (!dest.cognome?.trim()) miss.push("Cognome");
      if (!dest.codice_fiscale?.trim()) miss.push("Codice Fiscale");
    } else {
      if (!dest.ragione_sociale?.trim()) miss.push("Ragione sociale");
      if (!dest.partita_iva?.trim()) miss.push("Partita IVA");
      if (!dest.codice_sdi?.trim() && !dest.pec?.trim()) miss.push("Codice SDI o PEC");
    }
    return miss;
  };

  const maybeSaveAnag = async () => {
    const payload = tipo === "azienda"
      ? { denominazione: dest.ragione_sociale.trim(), partita_iva: dest.partita_iva, codice_fiscale: dest.codice_fiscale, codice_destinatario: dest.codice_sdi }
      : { nome: dest.nome.trim(), cognome: dest.cognome.trim(), codice_fiscale: dest.codice_fiscale };
    Object.assign(payload, { indirizzo: dest.indirizzo, numero_civico: dest.numero_civico, cap: dest.cap, comune: dest.citta, provincia: dest.provincia, email: dest.email, pec: dest.pec });
    try { await api.post("/anagrafiche/clienti", payload); toast.success("Destinatario salvato in anagrafica"); }
    catch { toast.error("Destinatario non salvato in anagrafica (procedo comunque)"); }
  };

  const save = async () => {
    const miss = validateDest();
    if (miss.length) { toast.error(`Campi destinatario mancanti: ${miss.join(", ")}`); return; }
    const vclean = vociNorm.filter((v) => (v.descrizione || "").trim() || v.importo).map((v) => ({ descrizione: v.descrizione.trim(), quantita: v.quantita, prezzo_unitario: v.prezzo_unitario, importo: v.importo }));
    if (vclean.length === 0) { toast.error("Aggiungi almeno una voce"); return; }
    const cliente_dati = { tipo, ...dest };
    const payload = {
      intestatario, cliente_dati,
      pratica_id: editing?.pratica_id || pratica?.id || null,
      pratica_numero: editing?.pratica_numero || pratica?.numero_pratica || "",
      cliente_key: editing?.cliente_key || clienteKey || null,
      voci: vclean, regime,
      regime_label: regime === "altro" ? (regimeLabel || "Altro regime") : (PROFORMA_REGIMI[regime]?.label || ""),
      iva_pct: num(ivaPct), ritenuta_pct: num(ritPct), cassa_pct: num(cassaPct),
      dicitura: dicitura.trim(), note: note.trim(), conto: conti[contoIdx] || null,
    };
    setSaving(true);
    try {
      let createdDoc = null;
      if (salvaAnag && !editing) await maybeSaveAnag();
      if (editing) {
        await api.patch(`/studio/proforme/${editing.id}`, payload);
        toast.success("Proforma aggiornata");
        if (editing.pratica_id && editing.allegato_id) {
          try { const { data: upd } = await api.get(`/studio/proforme/${editing.id}`); await replaceProformaAttachment(upd, studio); toast.success("Allegato della pratica aggiornato"); }
          catch { /* best-effort */ }
        }
      } else {
        const res = await api.post("/studio/proforme", payload);
        createdDoc = res.data;
        if (pratica?.id && createdDoc?.pratica_id) {
          try { await attachProformaToPratica(createdDoc, studio); toast.success("Proforma creata e allegata alla pratica"); }
          catch { toast.success("Proforma creata"); toast.error("Allegato automatico alla pratica non riuscito"); }
        } else {
          toast.success("Proforma creata");
        }
      }
      onSaved && onSaved(createdDoc);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const fld = (label, k, extra = {}) => (
    <div className={extra.full ? "sm:col-span-2" : ""}>
      <Label className="text-slate-300 text-xs">{label}{extra.req ? " *" : ""}</Label>
      <Input value={dest[k] || ""} onChange={(e) => setD(k, e.target.value)} onBlur={extra.validate ? () => setBlurred((b) => ({ ...b, [k]: true })) : undefined} data-testid={`proforma-dest-${k}`} className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
      {extra.validate === "cf" && <ValidationHint result={validateCodiceFiscale(dest[k])} show={!!blurred[k]} testId={`proforma-dest-${k}`} />}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="pgp-modal bg-[#121824] border-slate-700 text-slate-100 max-w-3xl overflow-y-auto" data-testid="proforma-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-400" /> {editing ? `Modifica Proforma ${editing.numero}` : "Nuova Fattura Proforma"}
            {(pratica?.numero_pratica || editing?.pratica_numero) && <span className="text-sm font-normal text-slate-500">· {pratica?.numero_pratica || editing?.pratica_numero}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Destinatario */}
          <div className="rounded-lg border border-slate-800 p-3 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-slate-200 text-sm font-semibold">Destinatario fattura</Label>
              <div className="inline-flex rounded-md border border-slate-700 overflow-hidden" data-testid="proforma-tipo-toggle">
                <button type="button" onClick={() => setTipo("persona")} data-testid="proforma-tipo-persona" className={`px-3 py-1.5 text-xs flex items-center gap-1 ${tipo === "persona" ? "bg-teal-500 text-[#0a0d14] font-semibold" : "text-slate-300"}`}><User className="h-3.5 w-3.5" /> Persona fisica</button>
                <button type="button" onClick={() => setTipo("azienda")} data-testid="proforma-tipo-azienda" className={`px-3 py-1.5 text-xs flex items-center gap-1 ${tipo === "azienda" ? "bg-teal-500 text-[#0a0d14] font-semibold" : "text-slate-300"}`}><Building2 className="h-3.5 w-3.5" /> Azienda / Professionista</button>
              </div>
            </div>

            <div className="flex justify-start" data-testid="proforma-copia-cliente-wrap">
              <CopiaDatiClienteButton data={dest} testId="proforma-copia-dati-cliente" />
            </div>

            {/* ricerca cliente */}
            <div>
              <Button type="button" onClick={() => setPickOpen((v) => !v)} data-testid="proforma-pick-cliente" className="h-9 bg-teal-500 hover:bg-teal-600 text-[#0a0d14] font-semibold gap-1"><Search className="h-4 w-4" /> Copia dati cliente da anagrafica</Button>
              <span className="ml-2 text-[11px] text-slate-500">Cerca un cliente già presente e compila automaticamente nome, indirizzo, CF/P.IVA, SDI, email e PEC.</span>
              {pickOpen && (
                <div className="mt-2 rounded-lg p-3 pgp-cli-dropdown" data-testid="proforma-cliente-picker">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-500" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, cognome o ragione sociale…" autoFocus className="pl-9 h-9 bg-slate-900/60 border-slate-700" data-testid="proforma-cliente-search" />
                  </div>
                  <div className="mt-2 max-h-48 overflow-y-auto">
                    {searching ? (
                      <div className="py-4 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-teal-400" /></div>
                    ) : results.length === 0 ? (
                      <div className="py-3 px-1 flex flex-col gap-2">
                        <span className="text-xs pgp-cli-sub">{searched ? "Nessun cliente trovato." : "Digita per cercare."}</span>
                        <Button type="button" onClick={() => { setDest({ ...EMPTY_DEST }); setPickOpen(false); toast.info("Compila i dati del nuovo destinatario qui sotto"); }} data-testid="proforma-nuovo-destinatario" className="h-8 bg-teal-500 hover:bg-teal-600 text-[#0a0d14] font-semibold gap-1 w-fit"><UserPlus className="h-4 w-4" /> Inserisci nuovo destinatario</Button>
                      </div>
                    ) : results.map((r) => (
                      <button key={r.id} type="button" onClick={() => chooseCliente(r)} data-testid={`proforma-cliente-opt-${r.id}`} className="w-full text-left px-2 py-2 rounded pgp-cli-opt">
                        <div className="text-sm">{displayName(r)}</div>
                        <div className="text-[11px] pgp-cli-sub">{[r.codice_fiscale, r.partita_iva, r.comune].filter(Boolean).join(" · ")}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* campi destinatario */}
            <div className="grid sm:grid-cols-2 gap-3">
              {tipo === "persona" ? (
                <>
                  {fld("Cognome", "cognome", { req: true })}
                  {fld("Nome", "nome", { req: true })}
                  {fld("Codice Fiscale", "codice_fiscale", { req: true, validate: "cf" })}
                  <div />
                </>
              ) : (
                <>
                  {fld("Ragione sociale / Denominazione", "ragione_sociale", { req: true, full: true })}
                  {fld("Partita IVA", "partita_iva", { req: true })}
                  {fld("Codice Fiscale (se diverso)", "codice_fiscale", { validate: "cf" })}
                  {fld("Codice SDI", "codice_sdi")}
                  <div />
                </>
              )}
              <div className="sm:col-span-2 relative">
                <Label className="text-slate-300 text-xs">Indirizzo (via) *</Label>
                <Input
                  value={dest.indirizzo || ""}
                  onChange={(e) => onAddrChange(e.target.value)}
                  onFocus={() => { if (addrSug.length) setAddrOpen(true); }}
                  onBlur={() => setTimeout(() => setAddrOpen(false), 200)}
                  autoComplete="off"
                  data-testid="proforma-dest-indirizzo"
                  placeholder="Inizia a digitare la via…"
                  className="mt-1 h-9 bg-slate-900/60 border-slate-700"
                />
                {addrOpen && (addrLoading || addrSug.length > 0) && (
                  <div className="absolute z-50 left-0 right-0 mt-1 rounded-lg p-1 pgp-cli-dropdown max-h-56 overflow-y-auto shadow-xl" data-testid="proforma-addr-suggestions">
                    {addrLoading ? (
                      <div className="p-3 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-teal-400" /></div>
                    ) : addrSug.map((s, i) => (
                      <button key={i} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pickAddr(s)} data-testid={`proforma-addr-opt-${i}`} className="w-full text-left px-3 py-2 rounded pgp-cli-opt text-sm">{s.display_name}</button>
                    ))}
                  </div>
                )}
              </div>
              {fld("Numero civico", "numero_civico")}
              {fld("CAP", "cap", { req: true })}
              {fld("Città", "citta", { req: true })}
              {fld("Provincia", "provincia", { req: true })}
              <div />
              {fld("Email", "email")}
              {fld(tipo === "azienda" ? "PEC (fatt. elettronica)" : "PEC", "pec", { req: tipo === "azienda" })}
            </div>

            {!editing && (
              <label className="flex items-center gap-2 cursor-pointer" data-testid="proforma-salva-anag-wrap">
                <Checkbox checked={salvaAnag} onCheckedChange={(v) => setSalvaAnag(!!v)} data-testid="proforma-salva-anag" className="data-[state=checked]:bg-teal-500 data-[state=checked]:border-teal-500" />
                <span className="text-sm text-slate-300">Salva anche come nuovo cliente in anagrafica</span>
              </label>
            )}
          </div>

          {/* Regime */}
          <div className="pgp-fattura-section space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-slate-300 text-xs">Regime fiscale</Label>
              <select value={regime} onChange={(e) => applyRegime(e.target.value)} data-testid="proforma-regime" className="mt-1 h-9 w-full rounded-md bg-slate-900/60 border border-slate-700 px-2 text-sm text-slate-100">
                {REGIME_OPTIONS.map(([k, l]) => <option key={k} value={k} className="bg-slate-900">{l}</option>)}
              </select>
            </div>
            {regime === "altro" && (
              <div>
                <Label className="text-slate-300 text-xs">Nome regime</Label>
                <Input value={regimeLabel} onChange={(e) => setRegimeLabel(e.target.value)} data-testid="proforma-regime-label" className="mt-1 h-9 bg-slate-900/60 border-slate-700" />
              </div>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-slate-300 text-xs">IVA %</Label>
              <Input type="number" step="1" value={ivaPct} onChange={(e) => setIvaPct(e.target.value)} disabled={!regimeEditable} data-testid="proforma-iva-pct" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono disabled:opacity-50" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Ritenuta %</Label>
              <Input type="number" step="1" value={ritPct} onChange={(e) => setRitPct(e.target.value)} disabled={!regimeEditable} data-testid="proforma-ritenuta-pct" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono disabled:opacity-50" />
            </div>
            <div>
              <Label className="text-slate-300 text-xs">Cassa prev. %</Label>
              <Input type="number" step="0.5" value={cassaPct} onChange={(e) => setCassaPct(e.target.value)} data-testid="proforma-cassa-pct" className="mt-1 h-9 bg-slate-900/60 border-slate-700 font-mono" />
            </div>
          </div>

          {/* Voci */}
          <div>
            <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
              <Label className="text-slate-300 text-xs">Voci / Prestazioni</Label>
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => setPredefOpen((v) => !v)} data-testid="proforma-scegli-predef" className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300"><ListChecks className="h-3.5 w-3.5" /> Scegli da predefinite</button>
                <button type="button" onClick={() => addVoce()} data-testid="proforma-add-voce" className="flex items-center gap-1 text-xs text-teal-400 hover:text-teal-300"><Plus className="h-3.5 w-3.5" /> Voce</button>
              </div>
            </div>
            {predefOpen && (
              <div className="mb-2 rounded-lg p-2 pgp-cli-dropdown max-h-48 overflow-y-auto" data-testid="proforma-predef-list">
                {predef.length === 0 ? <div className="text-xs pgp-cli-sub px-1 py-1">Nessuna voce predefinita. Salvane una con la stella.</div> :
                  predef.map((p, i) => (
                    editPredef && editPredef.i === i ? (
                      <div key={i} className="grid grid-cols-[1fr_52px_72px_auto_auto] gap-1 items-center px-1 py-1" data-testid={`proforma-predef-edit-${i}`}>
                        <Input value={editPredef.descrizione} onChange={(e) => setEditPredef((s) => ({ ...s, descrizione: e.target.value }))} className="h-8 bg-slate-900/60 border-slate-700 text-sm" data-testid={`proforma-predef-edit-desc-${i}`} />
                        <Input type="number" value={editPredef.quantita} onChange={(e) => setEditPredef((s) => ({ ...s, quantita: e.target.value }))} className="h-8 bg-slate-900/60 border-slate-700 font-mono text-xs" data-testid={`proforma-predef-edit-qta-${i}`} />
                        <EuroDotInput value={editPredef.prezzo_unitario} onChange={(val) => setEditPredef((s) => ({ ...s, prezzo_unitario: val }))} className="h-8 bg-slate-900/60 border-slate-700 text-xs" testId={`proforma-predef-edit-unit-${i}`} />
                        <button type="button" onClick={saveEditPredef} title="Salva" data-testid={`proforma-predef-save-${i}`} className="text-emerald-400 hover:text-emerald-300"><Check className="h-4 w-4" /></button>
                        <button type="button" onClick={() => setEditPredef(null)} title="Annulla" className="text-slate-400 hover:text-slate-200"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div key={i} className="flex items-center gap-1 px-1" data-testid={`proforma-predef-row-${i}`}>
                        <button type="button" onClick={() => choosePredef(p)} data-testid={`proforma-predef-${i}`} className="flex-1 min-w-0 text-left py-1.5 px-1 rounded pgp-cli-opt flex justify-between gap-2">
                          <span className="text-sm truncate">{p.descrizione}</span>
                          <span className="text-xs pgp-cli-sub font-mono shrink-0">{(p.quantita ?? 1)} × {formatEuro(p.prezzo_unitario)}</span>
                        </button>
                        <button type="button" onClick={() => startEditPredef(i)} title="Modifica" data-testid={`proforma-predef-edit-btn-${i}`} className="text-amber-400 hover:text-amber-300 shrink-0"><Pencil className="h-3.5 w-3.5" /></button>
                        <button type="button" onClick={() => delPredef(i)} title="Elimina" data-testid={`proforma-predef-del-${i}`} className="text-red-400 hover:text-red-300 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    )
                  ))}
              </div>
            )}
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr_64px_128px_128px_auto_auto] gap-2 px-0.5 text-[10px] uppercase tracking-wider text-slate-500 font-mono" data-testid="proforma-voci-header">
                <span>Descrizione</span><span className="text-center">Q.tà</span><span className="text-right">Prezzo</span><span className="text-right">Importo</span><span /><span />
              </div>
              {voci.map((v, i) => (
                <div key={i} className="grid grid-cols-[1fr_64px_128px_128px_auto_auto] gap-2 items-center" data-testid={`proforma-voce-${i}`}>
                  <Input value={v.descrizione} onChange={(e) => setVoce(i, "descrizione", e.target.value)} placeholder="Descrizione" className="h-9 bg-slate-900/60 border-slate-700" data-testid={`proforma-voce-desc-${i}`} />
                  <Input type="number" step="1" value={v.quantita} onChange={(e) => setVoce(i, "quantita", e.target.value)} placeholder="Q.tà" className="h-9 bg-slate-900/60 border-slate-700 font-mono" data-testid={`proforma-voce-qta-${i}`} />
                  <EuroDotInput value={v.prezzo_unitario} onChange={(val) => setVoce(i, "prezzo_unitario", val)} placeholder="Prezzo" className="h-9 bg-slate-900/60 border-slate-700" testId={`proforma-voce-unit-${i}`} />
                  <EuroDotInput value={vociNorm[i]?.importo ?? 0} readOnly title="Importo = Q.tà × Prezzo (calcolato automaticamente)" className="h-9 bg-slate-950/70 border-slate-800 text-slate-400 cursor-not-allowed" testId={`proforma-voce-imp-${i}`} />
                  <button type="button" onClick={() => saveAsPredef(v)} title="Salva come predefinita" data-testid={`proforma-voce-star-${i}`} className="text-amber-400 hover:text-amber-300"><Star className="h-4 w-4" /></button>
                  <button type="button" onClick={() => delVoce(i)} title="Rimuovi" data-testid={`proforma-voce-del-${i}`} className="text-red-400 hover:text-red-300"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Dicitura */}
          <div>
            <Label className="text-slate-300 text-xs">Dicitura legale</Label>
            {diciture.filter((d) => d.regime === regime).length > 0 && (
              <select value={dicModelId} onChange={(e) => { setDicModelId(e.target.value); const t = diciture.find((d) => d.id === e.target.value); if (t) setDicitura(t.testo); }} data-testid="proforma-dicitura-modello" className="mt-1 mb-1 h-9 w-full rounded-md bg-slate-900/60 border border-slate-700 px-2 text-sm text-slate-100">
                <option value="">— Scegli un modello di dicitura —</option>
                {diciture.filter((d) => d.regime === regime).map((d) => <option key={d.id} value={d.id} className="bg-slate-900">{d.nome}{d.principale ? " (principale)" : ""}</option>)}
              </select>
            )}
            <Textarea value={dicitura} onChange={(e) => setDicitura(e.target.value)} rows={2} data-testid="proforma-dicitura" className="mt-1 bg-slate-900/60 border-slate-700 text-sm" />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Note</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="proforma-note" className="mt-1 bg-slate-900/60 border-slate-700 text-sm" />
          </div>

          <div data-testid="proforma-coordinate-bancarie">
            <Label className="text-slate-300 text-xs">Coordinate bancarie</Label>
            {conti.length > 0 ? (
              <select value={contoIdx} onChange={(e) => setContoIdx(Number(e.target.value))} data-testid="proforma-conto" className="mt-1 h-9 w-full rounded-md bg-slate-900/60 border border-slate-700 px-2 text-sm text-slate-100">
                {conti.map((c, i) => <option key={i} value={i} className="bg-slate-900">{[c.banca, c.nome].filter(Boolean).join(" · ") || `Conto ${i + 1}`}{c.predefinito ? " (predefinito)" : ""}{c.iban ? ` — ${c.iban}` : ""}</option>)}
              </select>
            ) : (
              <div className="mt-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-700" data-testid="proforma-conto-empty">
                Nessun conto corrente configurato.{" "}
                <a href="/app/impostazioni#sez-conti-correnti" target="_blank" rel="noreferrer" className="underline font-semibold hover:text-amber-800" data-testid="proforma-conto-link">Aggiungi un conto corrente nelle impostazioni dello studio</a>.
              </div>
            )}
          </div>

          {/* Totali */}
          <div className="rounded-lg border border-slate-800 bg-slate-900/50 p-4 text-sm font-mono space-y-1">
            <div className="flex justify-between text-slate-400"><span>Compenso / Imponibile</span><span className="text-slate-200">{formatEuro(calc.compenso)}</span></div>
            {num(cassaPct) > 0 && <div className="flex justify-between text-slate-400"><span>Cassa prev. ({cassaPct}%)</span><span className="text-slate-200">{formatEuro(calc.cassa)}</span></div>}
            {num(ivaPct) > 0 && <div className="flex justify-between text-slate-400"><span>IVA {ivaPct}%</span><span className="text-slate-200">{formatEuro(calc.iva)}</span></div>}
            <div className="flex justify-between text-slate-300 border-t border-slate-800 pt-1"><span>Totale documento</span><span>{formatEuro(calc.totale_lordo)}</span></div>
            {num(ritPct) > 0 && <div className="flex justify-between text-red-400"><span>Ritenuta d'acconto ({ritPct}%)</span><span>− {formatEuro(calc.ritenuta)}</span></div>}
            {calc.bollo > 0 && <div className="flex justify-between text-slate-400" data-testid="proforma-bollo-row"><span>Imposta di bollo</span><span className="text-slate-200">{formatEuro(calc.bollo)}</span></div>}
            <div className="flex justify-between items-center border-t border-slate-700 pt-2 mt-1">
              <span className="text-xs uppercase tracking-wider text-slate-500">Netto a pagare</span>
              <span className="text-xl font-extrabold text-emerald-400" data-testid="proforma-netto">{formatEuro(calc.totale_netto)}</span>
            </div>
          </div>
          </div>

          <Button onClick={save} disabled={saving} data-testid="proforma-save" className="w-full bg-teal-500 hover:bg-teal-600 text-[#0a0d14] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> {editing ? "Salva modifiche" : "Crea proforma"}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
